"use client"

import { FC, useCallback, useMemo } from "react"
import { useContent } from "@/src/context/ContentContext"
import { useLanguage } from "@/src/context/LanguageContext"
import { useUI } from "@/src/lib/i18n/ui"

interface PricingOverviewProps {
  activeId: string
  setActiveId: (id: string) => void
}

const PricingOverview: FC<PricingOverviewProps> = ({ activeId, setActiveId }) => {
  const { pricing } = useContent()
  const { lang } = useLanguage()
  const t = useUI(lang)

  const handleSetActiveId = useCallback(
    (id: string) => setActiveId(id),
    [setActiveId]
  )

  const renderedOptions = useMemo(() => {
    return pricing.map((plan, index) => (
      <button
        key={plan.id}
        onClick={() => handleSetActiveId(plan.id)}
        className={`w-full text-left border-b pb-6 transition-colors group ${
          activeId === plan.id ? "border-gray-500" : "border-gray-700 hover:border-gray-500"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-gray-400 text-lg mr-4">
              {String(index + 1).padStart(2, "0")}.
            </span>
            <span className="text-4xl lg:text-4xl font-bold text-white group-hover:text-gray-300 transition-colors">
              {plan.price}
            </span>
            <span className="text-2xl lg:text-2xl text-gray-400 ml-2">{t("squareMeter")}</span>
          </div>
          <div className="text-gray-400 group-hover:text-white transition-colors">→</div>
        </div>
      </button>
    ))
  }, [activeId, handleSetActiveId, pricing])

  return (
    <section
      id="pricing"
      className="py-8 h-full bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#121212]"
    >
      <div className="container mx-auto">
        <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-12">
          {t("pricingHeading")}
        </h2>
        <div className="space-y-4 w-full">{renderedOptions}</div>
      </div>
    </section>
  )
}

export default PricingOverview
